import { Component, Suspense, useState, useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Html, Line, useGLTF } from "@react-three/drei";
import { gltfWithAuth } from "../gltfAuth";
import * as THREE from "three";
import { tokens } from "../../styles/tokens";
import type { ForkliftPose, ForkliftStatus } from "../../stores/telemetryStore";
import { useTelemetryStore } from "../../stores/telemetryStore";
import { modelUrlForAsset, ALL_MODEL_URLS, FORKLIFT_MODEL_SCALE, modelYawForSeries } from "../assetModels";
import { LABEL_Z_RANGE } from "../labelZ";
import styles from "./Forklift.module.css";

ALL_MODEL_URLS.forEach((url) => useGLTF.preload(url, true, true, gltfWithAuth));

const STATUS_COLOR: Record<ForkliftStatus, string> = {
  loaded:   tokens.semantic.success,
  empty:    tokens.semantic.info,
  idle:     tokens.semantic.warn,
  offline:  tokens.ink[500],
  linkdown: tokens.semantic.danger,
};

const PULSE_STATUSES = new Set<ForkliftStatus>(["linkdown", "offline"]);
const MAX_TRAIL = 30;
const TRAIL_SAMPLE_EVERY = 6; // ~10 Hz at 60 fps

// ---------------------------------------------------------------------------
// Series-aware fallback meshes
// ---------------------------------------------------------------------------

function PSeries() {
  return (
    <>
      <mesh position={[0, 0.7, 0]} castShadow>
        <boxGeometry args={[0.6, 1.4, 0.6]} />
        <meshStandardMaterial color="#2A3654" roughness={0.6} />
      </mesh>
      <mesh position={[0, 1.55, 0]} castShadow>
        <boxGeometry args={[0.35, 0.3, 0.35]} />
        <meshStandardMaterial color="#3B4A6B" roughness={0.5} />
      </mesh>
    </>
  );
}

function MSeries() {
  return (
    <mesh position={[0, 0.2, 0]} castShadow>
      <boxGeometry args={[1.2, 0.4, 0.8]} />
      <meshStandardMaterial color="#2A3654" roughness={0.6} />
    </mesh>
  );
}

function SSeries() {
  return (
    <mesh position={[0, 0.4, 0]} castShadow>
      <boxGeometry args={[0.8, 0.8, 0.8]} />
      <meshStandardMaterial color="#2A3654" roughness={0.6} />
    </mesh>
  );
}

function FSeries({ loaded }: { loaded: boolean }) {
  return (
    <>
      <mesh position={[0, 0.2, 0]} castShadow>
        <boxGeometry args={[1.2, 0.4, 0.8]} />
        <meshStandardMaterial color="#2A3654" roughness={0.6} />
      </mesh>
      <mesh position={[-0.2, 0.7, 0]} castShadow>
        <boxGeometry args={[0.5, 0.6, 0.6]} />
        <meshStandardMaterial color="#3B4A6B" roughness={0.6} />
      </mesh>
      <mesh position={[0.85, 0.06, -0.18]} castShadow>
        <boxGeometry args={[0.6, 0.05, 0.06]} />
        <meshStandardMaterial color="#9CA8C0" roughness={0.4} />
      </mesh>
      <mesh position={[0.85, 0.06, 0.18]} castShadow>
        <boxGeometry args={[0.6, 0.05, 0.06]} />
        <meshStandardMaterial color="#9CA8C0" roughness={0.4} />
      </mesh>
      {loaded && (
        <mesh position={[0.95, 0.18, 0]} castShadow>
          <boxGeometry args={[0.7, 0.3, 0.55]} />
          <meshStandardMaterial color={tokens.blue[300]} roughness={0.7} />
        </mesh>
      )}
    </>
  );
}

function ForkliftFallback({ series, loaded }: { series: ForkliftPose["series"]; loaded: boolean }) {
  switch (series) {
    case "P": return <PSeries />;
    case "M": return <MSeries />;
    case "S": return <SSeries />;
    default:  return <FSeries loaded={loaded} />;
  }
}

// ---------------------------------------------------------------------------
// GLB model
// ---------------------------------------------------------------------------

function ForkliftGLB({ url, scale, modelYaw }: { url: string; scale: number; modelYaw: number }) {
  const { scene } = useGLTF(url, true, true, gltfWithAuth);
  const clone = useMemo(() => {
    const c = scene.clone(true);
    c.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) (obj as THREE.Mesh).castShadow = true;
    });
    return c;
  }, [scene]);
  // The heading lives on the parent group; this is only which way the mesh was
  // authored (assetModels.modelYawForSeries).
  return (
    <group rotation={[0, modelYaw, 0]}>
      <primitive object={clone} scale={scale} />
    </group>
  );
}

class ModelErrorBoundary extends Component<
  { series: ForkliftPose["series"]; loaded: boolean; children: React.ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  override render() {
    if (this.state.failed)
      return <ForkliftFallback series={this.props.series} loaded={this.props.loaded} />;
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Distance-gated label — fades 25–40 m, hidden beyond 40 m
// ---------------------------------------------------------------------------

function RobotLabel({
  id,
  battery,
  groupRef,
}: {
  id: string;
  battery: number;
  groupRef: React.RefObject<THREE.Group | null>;
}) {
  const divRef = useRef<HTMLDivElement>(null);

  useFrame(({ camera }) => {
    if (!divRef.current || !groupRef.current) return;
    const dist = camera.position.distanceTo(groupRef.current.position);
    const opacity = dist < 25 ? 1 : dist < 40 ? Math.max(0, 1 - (dist - 25) / 15) : 0;
    divRef.current.style.opacity = String(opacity);
  });

  return (
    <Html position={[0, 1.8, 0]} center distanceFactor={20} occlude={false} zIndexRange={LABEL_Z_RANGE}>
      <div ref={divRef} className={styles.label}>
        <span className={styles.id}>{id}</span>
        <span className={styles.battery}>{Math.round(battery * 100)}%</span>
      </div>
    </Html>
  );
}

// ---------------------------------------------------------------------------
// Forklift
// ---------------------------------------------------------------------------

interface Props {
  pose: ForkliftPose;
  showTrail: boolean;
  showLabel: boolean;
}

export function Forklift({ pose, showTrail, showLabel }: Props) {
  const groupRef   = useRef<THREE.Group>(null);
  const ringMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const targetPos  = useRef(new THREE.Vector3(pose.x, 0, pose.y));
  const targetYaw  = useRef(-pose.yaw);
  const [trail, setTrail] = useState<[number, number, number][]>([]);
  const [hovered, setHovered] = useState(false);
  const trailFrame = useRef(0);

  const select     = useTelemetryStore((s) => s.select);
  const setFollow  = useTelemetryStore((s) => s.setFollow);
  const isSelected = useTelemetryStore((s) => s.selectedAssetId === pose.id);
  const ringColor  = STATUS_COLOR[pose.status];
  const modelUrl   = modelUrlForAsset(pose.id, pose.series);
  const modelScale = pose.modelScale ?? FORKLIFT_MODEL_SCALE;
  const shouldPulse = PULSE_STATUSES.has(pose.status);

  targetPos.current.set(pose.x, 0, pose.y);
  targetYaw.current = -pose.yaw;

  useFrame(({ clock }, dt) => {
    const g = groupRef.current;
    if (!g) return;

    g.position.lerp(targetPos.current, Math.min(1, dt * 6));
    // Shortest arc: without wrapping, a truck crossing +/-pi took the long way
    // round and spun a full turn on the spot.
    let dy = (targetYaw.current - g.rotation.y) % (Math.PI * 2);
    if (dy > Math.PI) dy -= Math.PI * 2;
    if (dy < -Math.PI) dy += Math.PI * 2;
    g.rotation.y += dy * Math.min(1, dt * 8);

    if (ringMatRef.current && shouldPulse) {
      const t = (Math.sin(clock.elapsedTime * (Math.PI * 2 / 1.6)) + 1) * 0.5;
      ringMatRef.current.emissiveIntensity = 0.6 + t * 0.4;
    }

    if (showTrail) {
      trailFrame.current++;
      if (trailFrame.current % TRAIL_SAMPLE_EVERY === 0) {
        const p: [number, number, number] = [g.position.x, 0.06, g.position.z];
        setTrail((prev) => {
          const next = [...prev, p];
          return next.length > MAX_TRAIL ? next.slice(next.length - MAX_TRAIL) : next;
        });
      }
    }
  });

  return (
    <>
      <group ref={groupRef}>
        <group
          onClick={(e) => { e.stopPropagation(); select(pose.id); }}
          onDoubleClick={(e) => { e.stopPropagation(); setFollow(pose.id); }}
          // ADR 279: hover affordance — cursor plus a faint pre-selection ring.
          onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = "pointer"; }}
          onPointerOut={() => { setHovered(false); document.body.style.cursor = "auto"; }}
        >
          <ModelErrorBoundary series={pose.series} loaded={pose.loaded ?? false}>
            <Suspense fallback={<ForkliftFallback series={pose.series} loaded={pose.loaded ?? false} />}>
              <ForkliftGLB url={modelUrl} scale={modelScale} modelYaw={modelYawForSeries(pose.series)} />
            </Suspense>
          </ModelErrorBoundary>
        </group>

        {/* status ring — torus, emissive, pulses on danger/offline */}
        <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.65, 0.08, 8, 32]} />
          <meshStandardMaterial
            ref={ringMatRef}
            color={ringColor}
            emissive={ringColor}
            emissiveIntensity={0.8}
            toneMapped={false}
          />
        </mesh>

        {isSelected && (
          <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.85, 0.95, 48]} />
            <meshBasicMaterial color={tokens.blue[500]} toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
        )}

        {/* ADR 279: hover = a translucent preview of the selection ring, so
            "this is clickable" and "this is selected" are the same visual
            language at two volumes. */}
        {hovered && !isSelected && (
          <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.85, 0.95, 48]} />
            <meshBasicMaterial color={tokens.blue[300]} transparent opacity={0.45}
                               toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
        )}

        {showLabel && (
          <RobotLabel id={pose.id} battery={pose.battery} groupRef={groupRef} />
        )}
      </group>

      {showTrail && trail.length >= 2 && (
        <Line
          points={trail}
          color={tokens.blue[300]}
          lineWidth={1.5}
          transparent
          opacity={0.6}
        />
      )}
    </>
  );
}
